import { Colors } from '@factorialco/deprecated-design-system'
import { ShiftManagementDefaultShiftsQuery } from 'generated/resources/graphql'
import { computed, makeObservable } from 'mobx'

import locations from 'modules/locations/store/collections/locations'
import workAreas from 'modules/locations/store/collections/workAreas'
import type Location from 'modules/locations/store/models/Location'
import type WorkArea from 'modules/locations/store/models/WorkArea'
import { nameFromHexCode } from 'modules/shift_management/lib/colors'
import ApiDefaultShift, {
  type IDefaultShift,
} from 'modules/shift_management/store/api/models/DefaultShift'
import teams from 'modules/teams/store/collections/teams'
import type Team from 'modules/teams/store/models/Team'

export type TDefaultShift =
  ShiftManagementDefaultShiftsQuery['shiftManagement']['defaultShifts'][number]

export interface Props {
  id: number
  startAt: string
  endAt: string
  title: string
  overnightShift: boolean
  color: Colors | null
  archived: boolean
  locationId: number | null | undefined
  workAreaId: number | null | undefined
  notes: string | null | undefined
  allCompany: boolean
  teamsIdsVisibilities: number[] | null
  locationsIdsVisibilities: number[] | null
  extraHours: boolean
}
class DefaultShift {
  id: number
  startAt: string
  endAt: string
  title: string
  overnightShift: boolean
  color: Colors | null
  archived: boolean
  locationId: number | null
  workAreaId: number | null
  notes: string | null
  allCompany: boolean
  teamsIdsVisibilities: number[] | null
  locationsIdsVisibilities: number[] | null
  extraHours: boolean

  constructor(props: Props) {
    this.id = props.id
    this.startAt = props.startAt
    this.endAt = props.endAt
    this.title = props.title
    this.overnightShift = props.overnightShift
    this.color = props.color
    this.archived = props.archived
    this.locationId = props.locationId || null
    this.workAreaId = props.workAreaId || null
    this.notes = props.notes || null
    this.allCompany = props.allCompany
    this.teamsIdsVisibilities = props.teamsIdsVisibilities
    this.locationsIdsVisibilities = props.locationsIdsVisibilities
    this.extraHours = props.extraHours

    makeObservable(this, {
      isActive: computed,
      location: computed,
      workArea: computed,
    })
  }

  /**
   * @deprecated Before using this method, consider using the `buildFromGraphQL` hook since
   * we are trying to move away from using mobx-rest and use GraphQL instead.
   */
  static buildFromProps(defaultShift: IDefaultShift): DefaultShift {
    const color = defaultShift.color ? nameFromHexCode(defaultShift.color) : null

    return new DefaultShift({
      id: defaultShift.id,
      startAt: defaultShift.start_at,
      endAt: defaultShift.end_at,
      title: defaultShift.title,
      overnightShift: defaultShift.overnight_shift,
      color,
      archived: defaultShift.archived,
      locationId: defaultShift.location_id,
      workAreaId: defaultShift.work_area_id,
      notes: defaultShift.notes,
      allCompany: defaultShift.all_company,
      teamsIdsVisibilities: defaultShift.visible_teams_ids,
      locationsIdsVisibilities: defaultShift.visible_locations_ids,
      extraHours: defaultShift.extra_hours,
    })
  }

  static buildFromGraphQL(fragment: TDefaultShift): DefaultShift {
    const color = fragment.color ? nameFromHexCode(fragment.color) : null

    return new DefaultShift({
      id: fragment.id,
      startAt: fragment.startAt,
      endAt: fragment.endAt,
      title: fragment.title,
      overnightShift: fragment.overnightShift,
      color,
      archived: fragment.archived,
      locationId: fragment.locationId,
      workAreaId: fragment.workAreaId,
      notes: fragment.notes,
      allCompany: fragment.allCompany,
      teamsIdsVisibilities: fragment.visibleTeamsIds,
      locationsIdsVisibilities: fragment.visibleLocationsIds,
      extraHours: fragment.extraHours,
    })
  }

  toPayload(): ApiDefaultShift {
    return new ApiDefaultShift({
      id: this.id,
      start_at: this.startAt,
      end_at: this.endAt,
      title: this.title,
      overnight_shift: this.overnightShift,
      color: this.color,
      archived: this.archived,
      location_id: this.locationId,
      work_area_id: this.workAreaId,
      notes: this.notes,
      all_company: this.allCompany,
      visible_teams_ids: this.teamsIdsVisibilities,
      visible_locations_ids: this.locationsIdsVisibilities,
      extra_hours: this.extraHours,
    })
  }

  get isActive(): boolean {
    return !this.archived
  }

  get location(): Location | undefined {
    return locations.find({
      id: this.locationId,
    })
  }

  get workArea(): WorkArea | undefined {
    return workAreas.find({
      id: this.workAreaId,
    })
  }

  get teamsAccessNames(): string[] {
    if (!this.teamsIdsVisibilities) return []

    return this.teamsIdsVisibilities
      .map((id) => teams.find({ id }))
      .filter((team): team is Team => team !== undefined)
      .map((team) => team.attributes.name)
  }

  get locationsAccessNames(): string[] {
    if (!this.locationsIdsVisibilities) return []

    return this.locationsIdsVisibilities
      .map((id) => locations.find({ id }))
      .filter((location): location is Location => location !== undefined)
      .map((location) => location.attributes.name)
  }

  get combinedAccessNames(): string[] {
    const names = new Set([...this.teamsAccessNames, ...this.locationsAccessNames])

    return Array.from(names)
  }
}

export default DefaultShift
